import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { countries, educationLevels } from '../mock';
import { Upload } from 'lucide-react';

const CubApplication = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    countryOfBirth: '',
    countryOfResidence: '',
    parentsCountryOfBirth: '',
    parentsCountryOfResidence: '',
    motherEducation: '',
    fatherEducation: '',
    ownEducation: '',
    motherOccupation: '',
    fatherOccupation: '',
    ownOccupation: '',
    isMinority: '',
    minorityCountry: '',
    gpa: '',
    isStudent: '',
    graduationInfo: '',
    howHeard: '',
    whyJoin: '',
    struggles: '',
    dinnerPerson: '',
    eulogy: '',
    videoUrl: '',
    consent: false
  });

  const [resume, setResume] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResume(file);
      toast.success(`Resume uploaded: ${file.name}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error('Please consent to be contacted.');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/applications/cub`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      toast.success('Application submitted successfully! We will review your application and contact you soon.');
    
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        dateOfBirth: '',
        gender: '',
        countryOfBirth: '',
        countryOfResidence: '',
        parentsCountryOfBirth: '',
        parentsCountryOfResidence: '',
        motherEducation: '',
        fatherEducation: '',
        ownEducation: '',
        motherOccupation: '',
        fatherOccupation: '',
        ownOccupation: '',
        isMinority: '',
        minorityCountry: '',
        gpa: '',
        isStudent: '',
        graduationInfo: '',
        howHeard: '',
        whyJoin: '',
        struggles: '',
        dinnerPerson: '',
        eulogy: '',
        videoUrl: '',
        consent: false
      });
      setResume(null);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="min-h-screen flex items-center justify-center text-white relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://www.lionsunleashed.org/wp-content/uploads/2020/07/Pic-About.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/90 via-blue-600/85 to-purple-600/90"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10 py-20">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight">
              <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }} className="block mb-2">
                Your Dreams Come
              </span>
              <span className="block font-black">
                to <span style={{ color: '#D4AF37' }}>Life</span>
              </span>
            </h1>
            
            <h2 className="text-3xl md:text-5xl font-bold mb-8">
              Become a Cub Today
            </h2>
            
            <p className="text-xl md:text-3xl mb-16 leading-relaxed font-light">
              Have the opportunity of a lifetime to be mentored by industry professionals who want to see you succeed!
            </p>
            
            <div className="max-w-4xl mx-auto p-8 bg-white/10 backdrop-blur-md rounded-3xl border border-white/30">
              <p className="text-xl md:text-2xl italic leading-relaxed">
                "With little labor, little are our gains; man's fortunes are according to his pains."
              </p>
              <p className="text-lg md:text-xl mt-4">
                - Robert Herrick
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-2xl border-0">
              <CardHeader className="text-center py-12 bg-gradient-to-br from-white to-amber-50">
                <CardTitle className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                  <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Application</span>{' '}
                  <span className="font-black" style={{ color: '#D4AF37' }}>Form</span>
                </CardTitle>
                <CardDescription className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Please fill out all required fields to complete your application. We review all applications carefully and will contact you within 2 weeks.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Personal Information</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input 
                        id="firstName" 
                        name="firstName" 
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input 
                        id="lastName" 
                        name="lastName" 
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email" 
                        name="email" 
                        type="email" 
                        value={formData.email}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input 
                        id="phone" 
                        name="phone" 
                        type="tel" 
                        value={formData.phone}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address">Address *</Label>
                    <Input 
                      id="address" 
                      name="address" 
                      value={formData.address}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                      <Input 
                        id="dateOfBirth" 
                        name="dateOfBirth" 
                        type="date" 
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    <div>
                      <Label>Gender *</Label>
                      <RadioGroup 
                        value={formData.gender}
                        onValueChange={(value) => handleSelectChange('gender', value)}
                        className="flex flex-wrap gap-4 mt-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="male" id="male" />
                          <Label htmlFor="male">Male</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="female" id="female" />
                          <Label htmlFor="female">Female</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="other" id="other" />
                          <Label htmlFor="other">Other</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="prefer-not-to-say" id="prefer-not" />
                          <Label htmlFor="prefer-not">Prefer Not To Say</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>

                {/* Background Information */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Background Information</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="countryOfBirth">What is your country of birth? *</Label>
                      <Select value={formData.countryOfBirth} onValueChange={(value) => handleSelectChange('countryOfBirth', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="countryOfResidence">What is your country of residence? *</Label>
                      <Select value={formData.countryOfResidence} onValueChange={(value) => handleSelectChange('countryOfResidence', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>What is/are your parents' country of birth?</Label>
                      <Select value={formData.parentsCountryOfBirth} onValueChange={(value) => handleSelectChange('parentsCountryOfBirth', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>What is/are your parents' country of residence?</Label>
                      <Select value={formData.parentsCountryOfResidence} onValueChange={(value) => handleSelectChange('parentsCountryOfResidence', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Education & Occupation */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Education & Occupation</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>What is your mother's level of education?</Label>
                      <Select value={formData.motherEducation} onValueChange={(value) => handleSelectChange('motherEducation', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {educationLevels.map(level => (
                            <SelectItem key={level} value={level}>{level}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>What is your father's level of education?</Label>
                      <Select value={formData.fatherEducation} onValueChange={(value) => handleSelectChange('fatherEducation', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {educationLevels.map(level => (
                            <SelectItem key={level} value={level}>{level}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>What is your level of education? *</Label>
                    <Select value={formData.ownEducation} onValueChange={(value) => handleSelectChange('ownEducation', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose" />
                      </SelectTrigger>
                      <SelectContent>
                        {educationLevels.map(level => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="motherOccupation">What is your mother's occupation?</Label>
                      <Input 
                        id="motherOccupation" 
                        name="motherOccupation" 
                        value={formData.motherOccupation}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="fatherOccupation">What is your father's occupation?</Label>
                      <Input 
                        id="fatherOccupation" 
                        name="fatherOccupation" 
                        value={formData.fatherOccupation}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="ownOccupation">What is your occupation? *</Label>
                    <Input 
                      id="ownOccupation" 
                      name="ownOccupation" 
                      value={formData.ownOccupation}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label>Are you a racial or ethnic minority? *</Label>
                    <RadioGroup 
                      value={formData.isMinority}
                      onValueChange={(value) => handleSelectChange('isMinority', value)}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="minority-yes" />
                        <Label htmlFor="minority-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="minority-no" />
                        <Label htmlFor="minority-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {formData.isMinority === 'yes' && (
                    <div>
                      <Label>If yes, what country?</Label>
                      <Select value={formData.minorityCountry} onValueChange={(value) => handleSelectChange('minorityCountry', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Academic Information */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Academic Information</h3>
                  
                  <div>
                    <Label htmlFor="gpa">GPA (using 4.0 scale) *</Label>
                    <Input 
                      id="gpa" 
                      name="gpa" 
                      type="number" 
                      step="0.01" 
                      min="0" 
                      max="4" 
                      value={formData.gpa}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label>Are you currently a student? *</Label>
                    <RadioGroup 
                      value={formData.isStudent}
                      onValueChange={(value) => handleSelectChange('isStudent', value)}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="student-yes" />
                        <Label htmlFor="student-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="student-no" />
                        <Label htmlFor="student-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {formData.isStudent === 'yes' && (
                    <div>
                      <Label htmlFor="graduationInfo">If yes, where and what is your graduation date?</Label>
                      <Input 
                        id="graduationInfo" 
                        name="graduationInfo" 
                        value={formData.graduationInfo}
                        onChange={handleInputChange}
                        placeholder="e.g., Harvard University, May 2025"
                      />
                    </div>
                  )}
                </div>

                {/* Program Questions */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Program Questions</h3>
                  
                  <div>
                    <Label htmlFor="howHeard">Where did you hear about our program? *</Label>
                    <Input 
                      id="howHeard" 
                      name="howHeard" 
                      value={formData.howHeard}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="whyJoin">Why do you want to join the program and what do you hope to get out of it? *</Label>
                    <Textarea 
                      id="whyJoin" 
                      name="whyJoin" 
                      value={formData.whyJoin}
                      onChange={handleInputChange}
                      rows={5}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="struggles">What is a struggle you have had to overcome and how has it shaped you as a person? What are your career goals? *</Label>
                    <Textarea 
                      id="struggles" 
                      name="struggles" 
                      value={formData.struggles}
                      onChange={handleInputChange}
                      rows={5}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="dinnerPerson">If you could have dinner with one person, living or dead, who would it be? *</Label>
                    <Input 
                      id="dinnerPerson" 
                      name="dinnerPerson" 
                      value={formData.dinnerPerson}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="eulogy">What would you want to be said about you in the eulogy at your funeral? *</Label>
                    <Textarea 
                      id="eulogy" 
                      name="eulogy" 
                      value={formData.eulogy}
                      onChange={handleInputChange}
                      rows={4}
                      required
                    />
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Additional Materials</h3>
                  
                  <div>
                    <Label htmlFor="resume">Upload Resume *</Label>
                    <div className="mt-2">
                      <label 
                        htmlFor="resume" 
                        className="flex items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-lg appearance-none cursor-pointer hover:border-cyan-500 focus:outline-none"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <Upload className="w-8 h-8 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {resume ? resume.name : 'Click to upload resume (PDF, DOC, DOCX)'}
                          </span>
                        </div>
                        <input 
                          id="resume" 
                          type="file" 
                          className="hidden" 
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="videoUrl">Video Submission (optional)</Label>
                    <p className="text-sm text-gray-600 mb-2">If you choose to answer via a video, upload your video submission to Youtube as UNLISTED then add the link here</p>
                    <Input 
                      id="videoUrl" 
                      name="videoUrl" 
                      type="url" 
                      value={formData.videoUrl}
                      onChange={handleInputChange}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                </div>

                {/* Consent */}
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="consent" 
                    checked={formData.consent}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                  />
                  <Label htmlFor="consent" className="text-sm">
                    I consent to be contacted by a representative of Lions Unleashed. *
                  </Label>
                </div>

                {/* Submit Button */}
                <div className="flex gap-4">
                  <Button 
                    type="submit" 
                    size="lg"
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg py-6"
                  >
                    Submit Application
                  </Button>
                </div>

                <p className="text-sm text-gray-600 text-center italic">
                  *Application subject to approval. We will review your application and contact you within 2 weeks.
                </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CubApplication;